import React from 'react';
import { motion } from 'framer-motion';
import { Check, Star } from 'lucide-react';
import { useContent } from '../../lib/content';
import { pickLocale } from '../../lib/api';

export default function Pricing() {
  const { content, locale } = useContent();
  const data = content.pricing || {};
  const tiers = data.tiers || [];

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="section bg-bg-secondary border-y border-line"
    >
      <div className="container-x">
        <div className="text-center mb-14 md:mb-20 max-w-3xl mx-auto">
          <p className="text-eyebrow mb-4">{pickLocale(data.eyebrow, locale)}</p>
          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[0.95] mb-6" data-testid="pricing-title">
            {pickLocale(data.title, locale)}
          </h2>
          <p className="text-ink-muted text-base md:text-lg">
            {pickLocale(data.subtitle, locale)}
          </p>
        </div>

        <div
          className={`grid gap-5 md:gap-6 ${tiers.length === 2 ? 'md:grid-cols-2 max-w-4xl mx-auto' : tiers.length >= 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3'}`}
          data-testid="pricing-cards"
        >
          {tiers.map((t, i) => {
            const featured = !!t.featured;
            return (
              <motion.div
                key={i}
                data-testid={`pricing-card-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className={`relative rounded-md p-8 md:p-10 flex flex-col gap-6 transition-all duration-300 ${
                  featured
                    ? 'bg-bg border border-acid shadow-[0_0_60px_rgba(232,255,58,0.18)]'
                    : 'bg-bg border border-line hover:border-line-strong'
                }`}
              >
                {featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-acid text-bg text-[10px] uppercase tracking-[0.22em] font-mono font-medium px-3 py-1 rounded-full flex items-center gap-1">
                    <Star size={10} fill="currentColor" /> {locale === 'es' ? 'Más popular' : 'Most popular'}
                  </div>
                )}
                <div>
                  <h3 className="font-display text-3xl text-ink mb-2">{pickLocale(t.name, locale)}</h3>
                  <p className="text-sm text-ink-muted leading-relaxed min-h-[2.5em]">
                    {pickLocale(t.description, locale)}
                  </p>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="font-display text-5xl md:text-6xl text-ink leading-none">
                    {typeof t.price === 'object' ? pickLocale(t.price, locale) : t.price}
                  </span>
                  <span className="text-ink-muted text-sm">{t.currency || ''}</span>
                  <span className="text-ink-muted text-sm ml-1">{pickLocale(t.period, locale)}</span>
                </div>
                <div className="h-px bg-line" />
                <ul className="space-y-3 flex-1">
                  {(t.features || []).map((f, j) => (
                    <li key={j} className="flex items-start gap-3 text-sm text-ink">
                      <Check size={16} className={featured ? 'text-acid mt-0.5 flex-shrink-0' : 'text-ink-muted mt-0.5 flex-shrink-0'} />
                      <span>{pickLocale(f, locale)}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={scrollToContact}
                  data-testid={`pricing-cta-${i}`}
                  className={featured ? 'btn-acid w-full justify-center' : 'btn-ghost w-full justify-center'}
                >
                  {pickLocale(t.cta, locale) || (locale === 'es' ? 'Solicitar' : 'Request')}
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
